import React from "react";
import _ from "lodash";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

import { Box } from "@chakra-ui/react";

import {
  checkServiceHasChildren,
  getNonWorkDays,
  getNonWorkDates,
  generateDateArray,
} from "../../../store/helpers";
import { completed } from "../../../store/constants";

import ServiceTimePerServiceTable from "../../../components/organization/tables/ServiceTimePerServiceTable";

const ServiceTimePerService = ({
  branchId = null,
  reportIndex,
  branchIndex,
}) => {
  const {
    tickets,
    services,
    servicesSubscriptions,
    workWeek,
    organizationId,
    dateRange,
    branches,
  } = useSelector((state) => state.defaultReducer);
  const servicesClone = _.cloneDeep(services);
  const servicesSubscriptionsClone = _.cloneDeep(servicesSubscriptions);
  const workWeekClone = _.cloneDeep(workWeek);
  let ticketsClone = _.cloneDeep(tickets);
  const branchesClone = _.cloneDeep(branches);
  ticketsClone = ticketsClone.filter(
    (a) => a.organizationId === organizationId
  );

  let updatedServices = checkServiceHasChildren(servicesClone);
  let subscribedServices = [];

  if (branchId) {
    subscribedServices =
      servicesSubscriptionsClone.find((a) => a.branchId === branchId)
        ?.services || null;
    if (subscribedServices == null) {
      console.log(
        branchId,
        subscribedServices,
        "Branch is not subscribed to any services"
      );
      return null;
    }
    // update services to only subscribed services
    updatedServices = updatedServices.filter((a) =>
      subscribedServices.includes(a.id)
    );
    // update tickets to only subscribed services
    ticketsClone = ticketsClone.filter((a) =>
      subscribedServices.includes(a.serviceId)
    );

    let branchServices = servicesSubscriptionsClone.find(
      (service) => service.branchId === branchId
    )?.services;

    subscribedServices = checkServiceHasChildren(servicesClone).filter(
      (service) => branchServices?.includes(service.id) && !service.hasChildren
    );
  } else {
    //go through all branches and get all unique services]
    const orgBranches = branchesClone.filter(
      (branch) =>
        branch.organizationId === organizationId && Number(branch.reportStatus)
    );
    let orgServices = [];
    orgBranches.forEach((branch) => {
      const branchServices =
        servicesSubscriptionsClone.find(
          (service) => service.branchId === branch.id
        )?.services || [];
      orgServices.push(...branchServices);
    });

    updatedServices = checkServiceHasChildren(servicesClone).filter(
      (service) => orgServices?.includes(service.id) && !service.hasChildren
    );
  }

  // use starting date from date range picker dateRange
  const dateObject = dayjs.unix(dateRange[0]);
  const daysInMonth = dateObject.daysInMonth();
  const datesArray = generateDateArray(dateRange[0], dateRange[1]);

  // create object to hold stats
  const serviceStatsObject = {
    sessionInSeconds: 0,
    completed: 0,
    avg: 0,
    min: 0,
    max: 0,

    days: datesArray.map((fullDate, i) => {
      let day = dayjs(fullDate).date();
      return {
        day,
        sessionInSeconds: 0,
        completed: 0,
        avg: 0,
      };
    }),
  };

  // create array of objects to hold stats for each service

  const managementStats = updatedServices
    .filter((a) => !a.hasChildren)
    .map((a) => ({
      ..._.cloneDeep(serviceStatsObject),
      id: a.id,
      name: a.name,
    }));

  // loop through tickets and update stats
  ticketsClone
    .filter((a) => a.status === completed)
    .forEach((a) => {
      let serviceIndex = managementStats.findIndex((b) => b.id === a.serviceId);
      if (serviceIndex === -1) {
        managementStats.push({
          ..._.cloneDeep(serviceStatsObject),
          id: a.serviceId,
          name: a.serviceName,
        });
        serviceIndex = managementStats.findIndex((b) => b.id === a.serviceId);
      }

      // unix timestamp to Date using dayjs, then get the day index in the array with (-1)
      const dayIndex = managementStats[serviceIndex].days.findIndex(
        (b) => b.day === dayjs.unix(a.created).date()
      );
      managementStats[serviceIndex].completed += 1;
      managementStats[serviceIndex].days[dayIndex].completed += 1;
      managementStats[serviceIndex].sessionInSeconds += a.sessionInSeconds;
      managementStats[serviceIndex].days[dayIndex].sessionInSeconds +=
        a.sessionInSeconds;

      if (a.status === 3) {
        managementStats[serviceIndex].completed += 1;
        managementStats[serviceIndex].days[dayIndex].completed += 1;
      }
    });

  // non work days. use workWeek to get non work days
  const nonWorkDays = getNonWorkDays(workWeekClone);
  // get non work dates from the calender month
  const nonWorkDates = getNonWorkDates(dateObject, nonWorkDays, daysInMonth);

  // update pct completed, no show, and unattended
  managementStats.forEach((a, serviceIndex) => {
    // for each service loop days and calculate avg
    a.days.forEach((b, dayIndex) => {
      managementStats[serviceIndex].days[dayIndex].avg =
        b.completed !== 0 ? (b.sessionInSeconds / b.completed).toFixed(2) : 0;
    });

    a.avg =
      a.completed !== 0 ? (a.sessionInSeconds / a.completed).toFixed(2) : 0;
    a.min = Math.min(...a.days.map((b) => b.avg));
    a.max = Math.max(...a.days.map((b) => b.avg));

    // remove non work days from days array
    a.days = a.days.filter((b) => !nonWorkDates.includes(b.day.toString()));
  });

  // sort by sessionInSeconds in descending order, followed by all empty data sets sorted alphabetically
  managementStats.sort((a, b) => {
    // Sort by sessionInSeconds in descending order
    if (b.sessionInSeconds - a.sessionInSeconds !== 0) {
      return b.sessionInSeconds - a.sessionInSeconds;
    }
    // If sessionInSeconds is the same, sort alphabetically by name
    return a.name.localeCompare(b.name);
  });

  return (
    <Box>
      <ServiceTimePerServiceTable
        branchId={branchId}
        data={managementStats}
        subscribedServices={subscribedServices}
        reportIndex={reportIndex}
        branchIndex={branchIndex}
      />
    </Box>
  );
};

export default ServiceTimePerService;
